import { TextInput, Textarea, Select, Checkbox, Group, Stack, Fieldset, Text, SimpleGrid, Badge, Input } from "@mantine/core";
import { UserSelect } from "../../components/UserSelect.js";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Ereignis, EreignisStatus } from "@iso27001/shared";
import { ereignisseApi, ereignisseLinks } from "../../api/ereignisse.js";
import { ApiError } from "../../api/client.js";
import { LinkedItemsList } from "../../components/LinkedItemsList.js";
import { AttachmentsSection } from "../../components/AttachmentsSection.js";
import { EntityModal } from "../../components/EntityModal.js";
import { useRecordAccess } from "../../components/use-record-access.js";
import { isEreignisClosed } from "../../components/record-state.js";
import { ereignisStatusColor } from "../../components/status-colors.js";
import { classBadges } from "./classification.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { useLocale } from "../../i18n/use-locale.js";
import { datePickerFormat } from "../../i18n/format-date.js";

interface Props {
  opened: boolean;
  onClose: () => void;
  initial: Ereignis | null;
}

const FLAG_FIELDS = ["isEreignis", "isSecurityEvent", "isSecurityIncident", "isDataProtectionIncident", "isNonconformity"] as const;


function flagsToCheckboxValues(initial: Ereignis | null): string[] {
  if (!initial) return [];
  return FLAG_FIELDS.filter((f) => initial[f]);
}

/** Truncate a string to `max` chars, appending "…" if cut. */
function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max) + "…";
}

export function EreignisFormModal({ opened, onClose, initial }: Props) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const access = useRecordAccess(!!initial && isEreignisClosed(initial.status));
  const { readOnly } = access;
  const { t } = useTranslation();
  const locale = useLocale();
  const { label } = useEnumLabel();
  const apiError = useApiErrorMessage();

  const statusOptions = EreignisStatus.options.map((value) => ({ value, label: label("ereignisStatus", value) }));

  // The 5 classification flags as checkbox group options
  const classificationOptions = [
    { value: "isEreignis", label: t("ereignisse.classification.ereignis") },
    { value: "isSecurityEvent", label: t("ereignisse.classification.securityEvent") },
    { value: "isSecurityIncident", label: t("ereignisse.classification.securityIncident") },
    { value: "isDataProtectionIncident", label: t("ereignisse.classification.dataProtectionIncident") },
    { value: "isNonconformity", label: t("ereignisse.classification.nonconformity") },
  ];

  const form = useForm({
    initialValues: {
      code: initial?.code ?? "",
      // reportedAt stored as Date object for DateInput; null when not set
      reportedAt: initial?.reportedAt ? new Date(initial.reportedAt * 1000) : null as Date | null,
      reporterUserId: initial?.reporterUserId ?? null as number | null,
      // classification as array of flag field names that are true
      classification: flagsToCheckboxValues(initial),
      description: initial?.description ?? "",
      affectedAreas: initial?.affectedAreas ?? "",
      affectedInformation: initial?.affectedInformation ?? "",
      evidenceNotes: initial?.evidenceNotes ?? "",
      comment: initial?.comment ?? "",
      status: initial?.status ?? "Offen",
    },
  });

  const linkedMassnahmenQuery = useQuery({
    queryKey: ["ereignisse", initial?.id, "massnahmen"],
    queryFn: () => ereignisseLinks.listMassnahmen(initial!.id),
    enabled: !!initial?.id,
  });
  const linkedMassnahmen = linkedMassnahmenQuery.data?.items ?? [];

  const mut = useMutation({
    mutationFn: async (vals: typeof form.values) => {
      const classificationSet = new Set(vals.classification);
      const payload: Record<string, unknown> = {
        reportedAt: vals.reportedAt ? Math.floor(vals.reportedAt.getTime() / 1000) : undefined,
        reporterUserId: vals.reporterUserId ?? null,
        isEreignis: classificationSet.has("isEreignis"),
        isSecurityEvent: classificationSet.has("isSecurityEvent"),
        isSecurityIncident: classificationSet.has("isSecurityIncident"),
        isDataProtectionIncident: classificationSet.has("isDataProtectionIncident"),
        isNonconformity: classificationSet.has("isNonconformity"),
        description: vals.description,
        affectedAreas: vals.affectedAreas || null,
        affectedInformation: vals.affectedInformation || null,
        evidenceNotes: vals.evidenceNotes || null,
        comment: vals.comment || null,
        status: vals.status as Ereignis["status"],
      };
      // Include code only when editing or when user explicitly provided one on create
      if (isEdit || vals.code.trim() !== "") {
        payload.code = vals.code;
      }
      return isEdit
        ? ereignisseApi.update(initial!.id, payload as Parameters<typeof ereignisseApi.update>[1])
        : ereignisseApi.create(payload as Parameters<typeof ereignisseApi.create>[0]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ereignisse"] });
      notifications.show({ message: isEdit ? t("ereignisse.form.notify.updated") : t("ereignisse.form.notify.created"), color: "teal" });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "Conflict") {
        form.setFieldError("code", t("ereignisse.error.codeConflict"));
      } else {
        notifications.show({ message: apiError(e, { fallback: "ereignisse.error.saveFailed" }), color: "red" });
      }
    },
  });

  return (
    <EntityModal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={isEdit ? t("ereignisse.form.title") : t("ereignisse.newEvent")}
      code={initial?.code}
      badge={initial && <Badge color={ereignisStatusColor[initial.status]} variant="light">{label("ereignisStatus", initial.status)}</Badge>}
      readOnly={readOnly}
      closed={access.closed}
      onUnlock={access.unlock}
      onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
      saving={mut.isPending}
      submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
    >
      <Stack>
        {/* Read-only drops the code input (it is in the title), leaving three short values. */}
        <SimpleGrid cols={{ base: 1, sm: readOnly ? 3 : 2 }}>
          {/* The code is in the title; read-only, a second copy would only repeat it. */}
          {!readOnly && (
            <TextInput
              label={isEdit ? t("common.field.code") : t("common.field.codeOptional")}
              description={isEdit ? undefined : t("ereignisse.form.code.autoHint")}
              // Hint under the input, so the field lines up with its neighbours in the row.
              inputWrapperOrder={["label", "input", "description", "error"]}
              required={isEdit}
              maxLength={80}
              placeholder={isEdit ? undefined : t("common.field.codeAutoPlaceholder")}
              {...form.getInputProps("code")}
            />
          )}
          <DateInput
            label={t("ereignisse.form.reportedAt.label")}
            required
            readOnly={readOnly}
            placeholder={t("common.field.datePlaceholder")}
            clearable={!readOnly}
            valueFormat={datePickerFormat(locale)}
            value={form.values.reportedAt}
            onChange={(val) => form.setFieldValue("reportedAt", val)}
            error={form.errors.reportedAt}
          />
          <UserSelect
            label={t("ereignisse.form.reporter.label")}
            placeholder={t("common.field.userPlaceholder")}
            readOnly={readOnly}
            clearable={!readOnly}
            value={form.values.reporterUserId}
            onChange={(val) => form.setFieldValue("reporterUserId", val)}
            error={form.errors.reporterUserId}
          />
          <Select
            label={t("common.field.status")}
            data={statusOptions}
            readOnly={readOnly}
            {...form.getInputProps("status")}
          />
        </SimpleGrid>
        {readOnly && initial ? (
          <Input.Wrapper label={t("ereignisse.classification.label")}>
            <Group gap={6} mt={6}>
              {classBadges(initial, t).map((b) => (
                <Badge key={b.label} color={b.color} variant="light">{b.label}</Badge>
              ))}
            </Group>
          </Input.Wrapper>
        ) : (
          <Checkbox.Group
            label={t("ereignisse.classification.label")}
            value={form.values.classification}
            onChange={(val) => form.setFieldValue("classification", val)}
          >
            <Group mt={4} gap="sm">
              {classificationOptions.map((opt) => (
                <Checkbox key={opt.value} value={opt.value} label={opt.label} />
              ))}
            </Group>
          </Checkbox.Group>
        )}
        <Textarea label={t("common.field.description")} required readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("description")} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Textarea label={t("ereignisse.form.affectedAreas.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("affectedAreas")} />
          <Textarea label={t("ereignisse.form.affectedInformation.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("affectedInformation")} />
          <Textarea label={t("ereignisse.form.evidenceNotes.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("evidenceNotes")} />
          <Textarea label={t("ereignisse.form.comment.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("comment")} />
        </SimpleGrid>
        {/* Verknüpfte Maßnahmen — read-only backlinks (links are managed from the Maßnahme side) */}
        <Fieldset legend={t("ereignisse.form.linkedMassnahmen.title")}>
          {initial?.id ? (
            <LinkedItemsList
              items={linkedMassnahmen.map((m) => ({
                id: m.id,
                label: `${m.code} — ${truncate(m.description, 60)}`,
              }))}
              onUnlink={async () => { /* read-only: no-op */ }}
              onAddClick={() => { /* read-only: managed from Maßnahme side */ }}
              emptyText={t("ereignisse.form.linkedMassnahmen.emptyText")}
              canEdit={false}
            />
          ) : (
            <Text size="sm" c="dimmed">{t("ereignisse.form.linkedMassnahmen.saveHint")}</Text>
          )}
        </Fieldset>

        {initial?.id && (
          <Fieldset legend={t("attachments.sectionTitle")}>
            <AttachmentsSection entity="ereignis" entityId={initial.id} canEdit={!readOnly} />
          </Fieldset>
        )}
      </Stack>
    </EntityModal>
  );
}
